import DataSeriesModal from "$root/components/DataSeriesModal";
import InfiniteLoader from "$root/components/InfiniteLoader";
import { useEventBus } from "$root/event-bus";
import { SentimentBadge } from "$root/functional-areas/market-view/analysis/SentimentBadge";
import { outlookRepository } from "$root/functional-areas/outlook/repository";
import useOutlookComposition from "$root/hooks/useOutlookComposition";
import { useLocaleFormatters } from "$root/localization/hooks";
import { type ContextContent, withContext } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { UniverseFiltersContext } from "$root/widgets-architecture/contexts/filters";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { Text } from "@mdotm/shadcn/legacy/components";
import { ForEach } from "@mdotm/utils/react-extra";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useTranslation } from "react-i18next";
import ACRHistoricalValuePercentile from "./ACRHistoricalValuePercentile";
import { feelsConverter } from "$root/functional-areas/market-view/analysis/feels-converter";

const ACROutlookExpected = (props: ContextContent<typeof UniverseFiltersContext>) => {
	const { firstFilter, secondFilter, thirdFilter } = props;
	const { assetClass, geography, sector } = useOutlookComposition(firstFilter, secondFilter, thirdFilter);

	const { t } = useTranslation();

	const { formatDate } = useLocaleFormatters();

	// Default widget configuration
	const defaultConfiguration = {
		outlook_type: "ASSET_CLASS_REGIME",
		header: {
			title: t("ASSET_CLASS_REGIME.TITLE"),
			subTitle: false,
			actions: {
				tooltip: {
					toShow: true,
					title: t("ASSET_CLASS_REGIME.TOOLTIP"),
				},
			},
		},
	};

	const marketRegimeWithElementsQuery = outlookRepository.marketRegimeWithElements.useQuery({
		assetClass,
		geography,
		sector,
	});

	const { data, isLoading, refetch } = useMappedQuery(
		(marketRegimeWithElements) => {
			const { comparables, regimePeriod, type, value } = marketRegimeWithElements;
			const typedRegime = regimePeriod as unknown as {
				from?: string;
				to?: string;
				serialized: number[];
			};
			const formatDateFn = (date?: string) => {
				if (!date) {
					return "";
				}
				const [day, month, year] = date.split("/");
				return `${year ?? ""}-${month ?? ""}-${day ?? ""}`;
			};

			const from = formatDate(new Date(formatDateFn(typedRegime.from)));
			const to = formatDate(new Date(formatDateFn(typedRegime.to)));

			return {
				scenarioDate: `${from} - ${to}`,
				rows: comparables,
				detectedScenario: { type, value },
			};
		},
		[formatDate],
		marketRegimeWithElementsQuery,
	);

	const { detectedScenario, rows, scenarioDate } = data ?? {};

	useEventBus("market-update", () => {
		refetch().catch(noop);
	});

	useWidgetOptions(
		() => ({
			title: defaultConfiguration.header.title,
			subTitle: defaultConfiguration.header.subTitle ? defaultConfiguration.header.subTitle : false,
			tooltip: defaultConfiguration.header.actions.tooltip.toShow
				? defaultConfiguration.header.actions.tooltip.title
				: undefined,
		}),
		[
			defaultConfiguration.header.actions.tooltip.title,
			defaultConfiguration.header.actions.tooltip.toShow,
			defaultConfiguration.header.subTitle,
			defaultConfiguration.header.title,
		],
	);
	// to make common feels converter

	return (
		<>
			{isLoading ? (
				<InfiniteLoader />
			) : (
				<>
					<div className="mb-6 mt-3 flex flex-col space-y-4 border border-solid border-slate-300 p-2">
						<div className="flex justify-between items-center">
							<Text type="Body/M/Bold">{t("ASSET_CLASS_REGIME.SCENARIO.DETECTED_SCENARIO")}</Text>

							{detectedScenario?.value ? (
								<ForEach
									collection={[
										detectedScenario.value
											? { ...detectedScenario, type: "RISK" }
											: { type: undefined, value: undefined },
									].flatMap((x) => {
										const feel = feelsConverter(x);
										return feel ? [feel] : [];
									})}
								>
									{(item) => (
										<SentimentBadge sentiment={item.sentiment} indicator={item.indicator}>
											{item.label}
										</SentimentBadge>
									)}
								</ForEach>
							) : (
								"Something went wrong"
							)}
						</div>
						<div className="flex justify-between items-center">
							<Text type="Body/M/Bold">{t("ASSET_CLASS_REGIME.SCENARIO.REGIME_PERIOD")}</Text>
							<Text>{scenarioDate}</Text>
						</div>
					</div>
					<div style={{ marginBottom: 12, marginTop: 10 }}>
						<div className="flex">
							<Text type="Body/XL/Bold" style={{ marginTop: 5 }}>
								{t("ASSET_CLASS_REGIME.SCENARIO.SCENARIO_DESCRIPTION")}
							</Text>
							<DataSeriesModal
								firstFilter={firstFilter}
								secondFilter={secondFilter}
								thirdFilter={thirdFilter}
								indicator="REGIME"
								typeMode="OUTLOOK_FOCUS"
							/>
						</div>
						<Text type="Body/L/Book" style={{ marginTop: 5 }}>
							{/* @ts-ignore */}
							{t(`ASSET_CLASS_REGIME.SCENARIO.DESCRIPTION.${detectedScenario?.value ?? 0}`)}
						</Text>
					</div>
					<ACRHistoricalValuePercentile data={rows ?? []} />
				</>
			)}
		</>
	);
};

export default withContext(UniverseFiltersContext)(ACROutlookExpected);
